import {
  Component,
  ComponentRef,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  Type,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  viewChild
} from '@angular/core';

import { ComponentRenderer } from '../../renderers/component-renderer';
import { DefaultColumnRendererComponent } from './default-column-renderer.component';
import { CellRenderer } from './cell-renderer';

/**
 * Component that dynamically renders configured CellRenderer components to Columns
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'cbre-cell-renderer',
  template: `<ng-container #Container></ng-container> `,
  imports: [],
  encapsulation: ViewEncapsulation.None,
})
export class CellRendererComponent<T extends { CellRenderer: Type<CellRenderer<any>> }>
  implements OnInit, ComponentRenderer<CellRenderer<T>>
{
  /**
   * Data to use to render
   */
  readonly data = input<Record<string, any>>();

  /**
   * config
   */
  readonly config = input<T>();

  /**
   * ContainerRef to render the configured CellRenderer<T> type
   */
  private readonly container = viewChild('Container', { read: ViewContainerRef });

  public input: ComponentRef<CellRenderer<T>>;

  private readonly parentInjector = inject(Injector);

  constructor() {
    effect(() => {
      const data = this.data();
      if (data != null && this.input) {
        this.input.setInput('data', data);
      }
    });
    effect(() => {
      const cfg = this.config();
      if (cfg != null && this.input) {
        this.input.setInput('config', cfg);
        this.initHostBindingVars(cfg);
      }
    });
  }

  ngOnInit(): void {
    const rendererType: Type<CellRenderer<any>> = this.resolveComponentType();
    this.mountComponent(rendererType);
  }

  public resolveComponentType(): Type<CellRenderer<any>> {
    return this.config()?.CellRenderer ?? this.getDefaultComponent();
  }

  public getDefaultComponent(): Type<CellRenderer<any>> {
    return DefaultColumnRendererComponent;
  }

  public mountComponent(component: Type<CellRenderer<T>>) {
    this.input = this.container().createComponent(component, { injector: this.parentInjector });
    this.input.setInput('config', this.config());
    this.input.setInput('data', this.data());

    // init host binding variables
    this.initHostBindingVars(this.config());
  }

  // eslint-disable-next-line no-unused-vars
  initHostBindingVars(_config: T): void {
    // placeholder to bind css classes accordingly to config info
  }
}
